import { useEffect, useState } from 'react';
import { PermissionAccessProvider, PermissionAwareButton, PermissionAwareInput } from '../../../contexts/PermissionAccessContext';
import { config } from '../../../config/config';
import safetySystemService from '../../../services/safetySystem.service';
import type { MaskedBankNumber, SafetyFreeLiabilityRow, UnitPayroll, UnitPayrollReveal } from '../../../types/safetySystem';
import type { ChildRow } from './ticketForm';
import { newChildRow } from './ticketForm';
import { ACCOUNT_TYPE_OPTIONS, CHILD_FIELDS, INS_FLAG_FIELDS, fmtMoney, gridOptions, liveRows } from './unitForm';
import { DateField, Field, MoneyField, Section, SelectField, TextField, YesNoField, inputClass, str } from './unitFields';
import { ReadOnly, errorText, fmtDateTime } from './safetyDrawerShared';
import { ChildListsProps, ChildrenStatus, UnitChildGrid } from './UnitChildGrid';

/**
 * Payroll tab of the Unit drawer — V1 `@payroll_layout`, two columns:
 * [Start Date; Insurance (one row per instype: GWI flag + the policy in force, "Edit"
 * opens that type's policy rows); Payroll Deductions grid; Settlements card] |
 * [Direct Deposit: hourly / disclosure / group + two bank accounts with the numbers
 * MASKED. "Reveal" (sensitive gate) fetches the audited plaintext for this open only;
 * "Edit bank numbers" (same gate) posts digits-only `dd_routing*` / `dd_account*`].
 *
 * The insurance GWI flags are unit columns owned by the Driver tab's save body; the rest
 * of the form goes to PUT /units/{r}/payroll, the grids to PUT /units/{r}/children.
 *
 * Under the LIABILITY INSURANCE policy editor (and only there — V1 `editinsurance`
 * 18545-18602) sits the Free Liability block: the unit's `driverpay_reimburse` FREE
 * LIABILITY rows plus "Add week of free liability" (unit-edit level 2), which posts to
 * POST /units/{r}/free-liability on its own — it is not part of the drawer's Save.
 */

export interface UnitPayrollTabProps extends ChildListsProps {
  payroll: UnitPayroll | null;
  payrollLoading: boolean;
  payrollError: string;
  /** Null only while the drawer is open on nothing (no Free Liability fetch then). */
  unitrecno: number | null;
  unitnumber: string;
  /** The drawer's `<dialog>` (DateTimePicker portal target). */
  portalContainer: HTMLElement | null;
  /** Editable copy of the payroll.py PAYROLL_FIELDS (dd_* keys). */
  form: Record<string, any>;
  onFormChange: (key: string, value: unknown) => void;
  /** The drawer's unit record (insurance GWI flags). */
  unit: Record<string, any>;
  onUnitChange: (key: string, value: unknown) => void;
  /**
   * Bank numbers typed this open (digits only), keyed `dd_routing1` …; the drawer posts
   * only these. `''` is the explicit "Clear" marker (posted as a blank -> cipher removed);
   * a field typed and then emptied again is NOT in here (nothing posted).
   */
  bankEdits: Record<string, string>;
  onBankEdit: (key: string, digits: string) => void;
  onBankClear: (key: string) => void;
  /** Bumped whenever `bankEdits` is re-baselined to `{}` (load, partial-save rebaseline) so the inputs drop their text. */
  bankEditsVersion: number;
  editingBank: boolean;
  onEditBank: () => void;
  revealed: UnitPayrollReveal | null;
  onReveal: () => void;
  revealing: boolean;
  revealError: string;
  canEdit: boolean;
  canSensitive: boolean;
  fieldErrors: Record<string, string>;
}

const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1.5 py-1 align-middle';

const masked = (m: MaskedBankNumber | undefined): string => (m?.on_file ? `•••• ${str(m.last4)}`.trim() : 'none on file');

/**
 * Masked (`•••• 1234` / "none on file") -> revealed read-only plaintext -> editable input;
 * the wire value is digits only. Emptying the input posts NOTHING (the number on file is
 * kept); only the explicit "Clear" control marks the field to be blanked on save. The
 * parent keys this component on `bankEditsVersion`, so a re-baseline drops the typed text.
 */
function BankNumber({ label, mask, plain, editing, value, onEdit, onClear, error }: {
  label: string; mask: MaskedBankNumber | undefined; plain: string | null; editing: boolean; value: string | undefined;
  onEdit: (digits: string) => void; onClear: () => void; error?: string;
}) {
  const [text, setText] = useState(value ?? ''); // survives a tab switch through the drawer's bankEdits
  const cleared = value === '';
  return (
    <Field label={label}>
      {editing ? (
        <div className="flex items-center gap-2">
          <PermissionAwareInput aria-label={label} className={inputClass} value={text} inputMode="numeric" autoComplete="off"
            placeholder={cleared ? 'will be cleared on save' : (plain ?? masked(mask))}
            onChange={(e) => { setText(e.target.value); onEdit(e.target.value.replace(/\D/g, '')); }} />
          <button type="button" aria-label={`Clear ${label}`} disabled={cleared || !mask?.on_file}
            onClick={() => { setText(''); onClear(); }}
            className="text-xs font-medium text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline">
            Clear
          </button>
        </div>
      ) : plain !== null ? (
        <input aria-label={label} className={inputClass} value={plain || 'none on file'} readOnly disabled />
      ) : (
        <span aria-label={label} className="block px-2 py-1 text-sm">{masked(mask)}</span>
      )}
      {error && <p role="alert" className="mt-0.5 text-xs text-danger-fg">{error}</p>}
    </Field>
  );
}

const LIABILITY_TYPE = 'LIABILITY INSURANCE';

/**
 * Free Liability (V1 `editrecord_safety.pl` 18545-18602 + `funcs_safety.pl` 10026-10135):
 * the read-only list, and an inline Week Of / Amount form. Amount defaults to the first
 * liability policy row's amount (V1 passed `ins_1_insamt`). The list refetches after a
 * successful add and the confirmation names the Payment Begin date the server computed.
 */
function FreeLiabilityBlock({ unitrecno, policies, canEdit, portalContainer }: {
  unitrecno: number | null; policies: ChildRow[]; canEdit: boolean; portalContainer: HTMLElement | null;
}) {
  const [rows, setRows] = useState<SafetyFreeLiabilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [gen, setGen] = useState(0);                 // bumped to refetch after an add
  const [open, setOpen] = useState(false);
  const [weekOf, setWeekOf] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!unitrecno) { setRows([]); setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    safetySystemService.freeLiability(unitrecno, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setRows(data ?? []); })
      .catch((e) => { if (!controller.signal.aborted) setLoadError(errorText(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [unitrecno, gen]);

  const openForm = () => {
    const first = liveRows(policies)[0];
    setWeekOf('');
    setAmount(first ? str(first.insamt) : '');
    setFormError('');
    setNotice('');
    setOpen(true);
  };

  const submit = async () => {
    if (!unitrecno) return;
    const week = weekOf.trim().slice(0, 10);
    const amt = Number(amount);
    if (!week) { setFormError('Week Of is required.'); return; }
    if (amount.trim() === '' || !Number.isFinite(amt) || amt <= 0) { setFormError('Amount must be greater than zero.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const res = await safetySystemService.addFreeLiability(unitrecno, { week_of: week, amount: amt });
      setOpen(false);
      setNotice(`Free liability week added — payment begins ${fmtDateTime(res.firstpaymentdate) || res.firstpaymentdate}.`);
      setGen((g) => g + 1);
    } catch (e: any) {
      // The 422 carries `{message, errors: [{field, message}]}`: name the field problem too.
      const errors = e?.response?.data?.detail?.errors;
      const extra = Array.isArray(errors) ? errors.map((x: any) => x?.message).filter(Boolean).join(' ') : '';
      setFormError(extra ? `${errorText(e)}: ${extra}` : errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <fieldset className="mt-2 rounded-md border border-rule p-2">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">Free Liability</legend>
      {loadError && <p role="alert" className="mb-1 text-xs text-danger-fg">{loadError}</p>}
      {loading && !loadError && <p className="py-1 text-center text-xs text-muted">Loading…</p>}
      {!loading && !loadError && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Free Liability">
            <thead>
              <tr><th className={th}>Description</th><th className={th}>Amount</th><th className={th}>Payment Begin</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={3} className="px-2 py-2 text-center text-sm text-muted">No free liability weeks.</td></tr>
              )}
              {rows.map((r, i) => (
                // Rows carry no recno (V1 SELECT); the list is replaced wholesale on refetch, so the index is a stable key.
                <tr key={i} className="border-t border-rule">
                  <td className={td}>{r.description}</td>
                  <td className={`${td} whitespace-nowrap`}>{fmtMoney(r.amount)}</td>
                  <td className={`${td} whitespace-nowrap`}>{fmtDateTime(r.firstpaymentdate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {notice && <p role="status" className="mt-1 text-xs text-muted">{notice}</p>}
      {canEdit && !!unitrecno && !open && (
        <button type="button" onClick={openForm} className="mt-2 rounded-md border border-rule px-2 py-1 text-xs font-medium hover:bg-highlight">
          Add week of free liability
        </button>
      )}
      {canEdit && open && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <DateField label="Week Of" value={weekOf} onChange={setWeekOf} portalContainer={portalContainer} />
            <MoneyField label="Amount" value={amount} onChange={setAmount} />
          </div>
          {formError && <p role="alert" className="text-xs text-danger-fg">{formError}</p>}
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Save free liability week" onClick={submit} disabled={saving}
              className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" aria-label="Cancel free liability week" onClick={() => { setOpen(false); setFormError(''); }} disabled={saving}
              className="rounded-md border border-rule px-2 py-1 text-xs hover:bg-highlight">
              Cancel
            </button>
          </div>
        </div>
      )}
    </fieldset>
  );
}

export default function UnitPayrollTab({
  payroll, payrollLoading, payrollError, unitrecno, unitnumber, portalContainer, form, onFormChange, unit, onUnitChange,
  bankEdits, onBankEdit, onBankClear, bankEditsVersion, editingBank, onEditBank, revealed, onReveal, revealing, revealError, canEdit, canSensitive, fieldErrors,
  childRows, onChildRowsChange, lookups, childrenLoading, childrenError, rowErrors,
}: UnitPayrollTabProps) {
  const f = (key: string) => (v: unknown) => onFormChange(key, v);
  const gridsReady = !childrenLoading && !childrenError;
  const [editingType, setEditingType] = useState<string | null>(null);

  if (payrollLoading) return <div className="py-8 text-center text-sm text-muted">Loading payroll…</div>;
  if (payrollError) return <div role="alert" className="rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{payrollError}</div>;
  if (!payroll) return null;

  const dd = payroll.direct_deposit;
  const s = payroll.settlements;
  const historyUrl = `${config.LEGACY_CMS_URL}/index_cms.pl?action=setdriverpay_history&driver=${encodeURIComponent(unitnumber)}`;
  const deductionTypes = lookups.deductiontypes;

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* ---- Column 1: start date, insurance, deductions, settlements ---- */}
        <div className="space-y-3">
          <Section title="Payroll">
            <ReadOnly label="Start Date" value={fmtDateTime(payroll.unit.startdate)} />
          </Section>

          <fieldset className="rounded-md border border-rule p-2">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">Insurance</legend>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={th}>Type</th><th className={th}>GWI</th><th className={th}>Current Amt</th>
                    <th className={th}>Start</th><th className={th}>End</th><th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {payroll.insurance_current.map((row) => {
                    const flag = INS_FLAG_FIELDS[row.instype];
                    const editing = editingType === row.instype;
                    return (
                      <tr key={row.instype} className="border-t border-rule">
                        <td className={`${td} whitespace-nowrap`}>{row.instype}</td>
                        <td className={td}>
                          {flag ? (
                            <YesNoField label={`${row.instype} GWI`} value={unit[flag]} onChange={(v) => onUnitChange(flag, v)} />
                          ) : '—'}
                        </td>
                        <td className={td} aria-label={`${row.instype} current amount`}>{fmtMoney(row.insamt)}</td>
                        <td className={`${td} whitespace-nowrap`}>{fmtDateTime(row.insstartdate)}</td>
                        <td className={`${td} whitespace-nowrap`}>{fmtDateTime(row.insenddate)}</td>
                        <td className={td}>
                          <button type="button" aria-label={`${editing ? 'Close' : 'Edit'} ${row.instype} policies`} onClick={() => setEditingType(editing ? null : row.instype)}
                            className="text-xs font-medium text-accent-text hover:underline">
                            {editing ? 'Close' : 'Edit'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {editingType && (gridsReady ? (
              <div className="mt-2">
                <UnitChildGrid
                  title={`${editingType} policies`}
                  rows={childRows.insurance ?? []}
                  onRowsChange={(rows) => onChildRowsChange('insurance', rows)}
                  filter={(r) => str(r.instype).toUpperCase() === editingType}
                  columns={[
                    { key: 'insamt', label: 'Policy Amount', type: 'number', step: '0.01', className: 'w-28' },
                    { key: 'insstartdate', label: 'Policy Start', type: 'date' },
                    { key: 'insenddate', label: 'Policy End', type: 'date' },
                    { key: 'insinceptiondate', label: 'Policy Inception', type: 'date' },
                  ]}
                  newRow={() => newChildRow(CHILD_FIELDS.insurance, { instype: editingType })}
                  addLabel="Add policy"
                  removeLabel="Remove policy"
                  emptyText={`No ${editingType} policies.`}
                  errors={rowErrors.insurance}
                />
                {editingType === LIABILITY_TYPE && (
                  <FreeLiabilityBlock
                    unitrecno={unitrecno}
                    policies={(childRows.insurance ?? []).filter((r) => str(r.instype).toUpperCase() === LIABILITY_TYPE)}
                    canEdit={canEdit}
                    portalContainer={portalContainer}
                  />
                )}
              </div>
            ) : <ChildrenStatus title={`${editingType} policies`} loading={childrenLoading} error={childrenError} />)}
          </fieldset>

          {gridsReady ? (
            <UnitChildGrid
              title="Payroll Deductions"
              rows={childRows.deductions ?? []}
              onRowsChange={(rows) => onChildRowsChange('deductions', rows)}
              columns={[
                {
                  key: 'deductiontype', label: 'Deduction Type', type: 'select', options: gridOptions(deductionTypes), className: 'min-w-[10rem]',
                  // V1 pdtypechange: picking a type fills a blank amount with the type's default rate.
                  onChange: (row, value) => {
                    const t = deductionTypes.find((d) => String(d.recno) === value);
                    const blank = str(row.deductionamt).trim() === '' || Number(row.deductionamt) === 0;
                    return { ...row, deductiontype: value, ...(blank && t && str(t.defaultrate) !== '' ? { deductionamt: str(t.defaultrate) } : {}) };
                  },
                },
                { key: 'deductionrefno', label: 'Deduction Ref #', className: 'min-w-[6rem]' },
                { key: 'deductionamt', label: 'Deduction Amount', type: 'number', step: '0.01', className: 'w-28' },
                { key: 'deductionstartdate', label: 'Deduction Start', type: 'date' },
                { key: 'deductionenddate', label: 'Deduction End', type: 'date' },
              ]}
              newRow={() => newChildRow(CHILD_FIELDS.deductions)}
              addLabel="Add deduction"
              removeLabel="Remove deduction"
              emptyText="No payroll deductions."
              errors={rowErrors.deductions}
            />
          ) : <ChildrenStatus title="Payroll Deductions" loading={childrenLoading} error={childrenError} />}

          <fieldset className="rounded-md border border-rule p-2">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">Settlements</legend>
            {payroll.settlements_error && <p role="alert" className="mb-1 text-xs text-danger-fg">{payroll.settlements_error}</p>}
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-5">
              {([['Loans', s.loans], ['Deferments', s.deferments], ['Escrow', s.escrow], ['Additional', s.additional], ['Difference', s.difference]] as [string, number | null][]).map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs font-medium text-muted">{label}</dt>
                  <dd aria-label={`Settlement ${label.toLowerCase()}`} className={label === 'Difference' ? 'font-semibold' : ''}>{fmtMoney(value)}</dd>
                </div>
              ))}
            </dl>
            <a href={historyUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs font-medium text-accent-text hover:underline">
              View settlement history
            </a>
          </fieldset>
        </div>

        {/* ---- Column 2: direct deposit ---- */}
        <div className="space-y-3">
          <Section title="Direct Deposit">
            <YesNoField label="Hourly" value={form.payrollhourly} onChange={f('payrollhourly')} />
            <YesNoField label="Disclosure" value={form.payrolldisclosure} onChange={f('payrolldisclosure')} />
            <SelectField label="Payroll Group" value={form.payrollgroup} onChange={f('payrollgroup')} options={gridOptions(lookups.payrollgroups)} className="col-span-2" />
            {Object.entries(fieldErrors).filter(([k]) => !k.startsWith('dd_routing') && !k.startsWith('dd_account')).map(([field, message]) => (
              <p key={field} role="alert" className="col-span-2 text-xs text-danger-fg">{message}</p>
            ))}
            <div className="col-span-2 flex flex-wrap items-center gap-2">
              {canSensitive && revealed === null && (
                <button type="button" onClick={onReveal} disabled={revealing}
                  className="rounded-md border border-rule px-2 py-1 text-xs font-medium hover:bg-highlight disabled:cursor-not-allowed">
                  {revealing ? 'Revealing…' : 'Reveal bank numbers'}
                </button>
              )}
              {canSensitive && canEdit && !editingBank && (
                <PermissionAwareButton type="button" onClick={onEditBank} className="rounded-md border border-rule px-2 py-1 text-xs font-medium hover:bg-highlight">
                  Edit bank numbers
                </PermissionAwareButton>
              )}
              {revealError && <span role="alert" className="text-xs text-danger-fg">{revealError}</span>}
            </div>
          </Section>
          {(['1', '2'] as const).map((n) => (
            <Section key={n} title={`Account ${n}`}>
              <TextField label={`Bank Name ${n}`} value={form[`dd_bankname${n}`]} onChange={f(`dd_bankname${n}`)} />
              <SelectField label={`Account Type ${n}`} value={form[`dd_accounttype${n}`]} onChange={f(`dd_accounttype${n}`)} options={ACCOUNT_TYPE_OPTIONS} />
              <TextField label={`Name on Account ${n}`} value={form[`dd_nameonaccount${n}`]} onChange={f(`dd_nameonaccount${n}`)} className="col-span-2" />
              <BankNumber key={`routing${n}-${bankEditsVersion}`} label={`Routing ${n}`} mask={dd[`routing${n}`]} plain={revealed ? revealed[`routing${n}`] : null}
                editing={editingBank} value={bankEdits[`dd_routing${n}`]} onEdit={(d) => onBankEdit(`dd_routing${n}`, d)}
                onClear={() => onBankClear(`dd_routing${n}`)} error={fieldErrors[`dd_routing${n}`]} />
              <BankNumber key={`account${n}-${bankEditsVersion}`} label={`Account ${n}`} mask={dd[`account${n}`]} plain={revealed ? revealed[`account${n}`] : null}
                editing={editingBank} value={bankEdits[`dd_account${n}`]} onEdit={(d) => onBankEdit(`dd_account${n}`, d)}
                onClear={() => onBankClear(`dd_account${n}`)} error={fieldErrors[`dd_account${n}`]} />
              <TextField label={`Amount ${n}`} value={form[`dd_amount${n}`]} onChange={f(`dd_amount${n}`)} />
              <YesNoField label={`Hold ${n}`} value={form[`dd_hold${n}`]} onChange={f(`dd_hold${n}`)} />
            </Section>
          ))}
        </div>
      </div>
    </PermissionAccessProvider>
  );
}
